import React, { useState } from 'react';
import '../../styles/components/FormPage.css';
import { Input as FormInput } from '../FormField';
import RichTextEditor from '../ui/RichTextEditor';
import { Driver } from '../../types/ui/Logistics';
import { createDriverStrict } from '../../services/LogisticsService';
import { useFormSaveEvent, firstMissingField } from './_shared';
import { useNotifications } from '../NotificationProvider';
import { PHONE_PLACEHOLDER } from '../../constants/placeholders';

interface DriverFormPageProps {
  onSave: (driver: Driver) => void;
  onCancel: () => void;
}

export const DriverFormPage: React.FC<DriverFormPageProps> = ({ onSave, onCancel }) => {
  const { showError } = useNotifications();
  const [form, setForm] = useState({
    name: '', phone: '', email: '', vehicle_id: '',
    notes: '', emergency_contact: '', emergency_phone: '',
  });

  const handleSave = async () => {
    const missing = firstMissingField([
      { label: 'Name', value: form.name },
      { label: 'Phone', value: form.phone },
    ]);
    if (missing) { showError(missing); return; }
    const driver = await createDriverStrict({ ...form });
    onSave(driver);
  };

  // A failed save throws out of handleSave: the shared hook shows a 422's
  // field errors under their fields (keys are the API's own field names) and
  // otherwise a plain-language toast, and the form stays open.
  const fieldErrors = useFormSaveEvent(handleSave, { fallback: 'Failed to save the driver.' });
  const set = (field: keyof typeof form, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    fieldErrors.clear(field);
  };

  return (
    <div className="inline-form-page">
      <div className="form-row">
        <FormInput label="Name" type="text" value={form.name}
          error={fieldErrors.errorFor('name')}
          onChange={e => set('name', e.target.value)} placeholder="Driver's full name" required />
        <FormInput label="Phone" type="tel" value={form.phone}
          error={fieldErrors.errorFor('phone')}
          onChange={e => set('phone', e.target.value)} placeholder={PHONE_PLACEHOLDER} required />
      </div>
      <div className="form-row">
        <FormInput label="Email" type="email" value={form.email}
          error={fieldErrors.errorFor('email')}
          onChange={e => set('email', e.target.value)} placeholder="email@example.com" />
        <FormInput label="Vehicle ID" type="text" value={form.vehicle_id}
          error={fieldErrors.errorFor('vehicle_id')}
          onChange={e => set('vehicle_id', e.target.value)} placeholder="Assign later if unknown" />
      </div>
      <div className="form-row">
        <FormInput label="Emergency Contact" type="text" value={form.emergency_contact}
          error={fieldErrors.errorFor('emergency_contact')}
          onChange={e => set('emergency_contact', e.target.value)} placeholder="Contact name" />
        <FormInput label="Emergency Phone" type="tel" value={form.emergency_phone}
          error={fieldErrors.errorFor('emergency_phone')}
          onChange={e => set('emergency_phone', e.target.value)} placeholder={PHONE_PLACEHOLDER} />
      </div>
      <RichTextEditor label="Notes" value={form.notes}
        error={fieldErrors.errorFor('notes')}
        onChange={val => set('notes', val)} rows={3}
        placeholder="Any additional information..." />
    </div>
  );
};
